/**
 * Offer value models (pure, deterministic given an Rng).
 *
 * Mirrors the thin-slice validation semantics (plan §9bis.1 V3):
 * - monthly class: renewal census posterior; when r2 <= r1 the honest model is
 *   GEOMETRIC survival with the `r2_not_rising` fallback tag (same guard the
 *   builder-service sBG fit applies on real data);
 * - upfront class (annual/weekly prepaid): value = upfront collection, no
 *   projected renewals (0 observable in-window);
 * - trial: delayed conversion share × the underlying paid offer's value at the
 *   SAME horizon (MVP criterion 5 — comparable value horizons).
 */

import {
  OfferContractError,
  type OfferValueEvidence,
  type OneTimeValueEvidence,
  type PendingCancelEvidence,
  type RenewalCensusEvidence,
  type TrialValueEvidence,
  type WeeklyRenewalEvidence,
} from './contracts';
import { sampleBeta, type Rng } from './rng';

export interface OfferValueModel {
  model: 'geometric' | 'upfront' | 'trial_scaled' | 'weekly_geometric' | 'one_time';
  fallbackReason?: 'r2_not_rising';
  /** Draw one posterior value-per-subscriber sample at the given horizon. */
  draw(rng: Rng, horizonMonths: number, drawUnderlying?: (rng: Rng) => number): number;
}

/**
 * Weekly cycle horizon derived from the value horizon — never a hardcoded 12
 * (kernel mirror of the shared cadence rule; 12 months → floor(365/7) = 52).
 * The kernel stays dependency-free, so the constant lives here too.
 */
export function weeklyCyclesForHorizonMonths(horizonMonths: number): number {
  if (!Number.isFinite(horizonMonths) || horizonMonths <= 0) {
    throw new Error('horizonMonths must be a positive finite number');
  }
  return Math.floor((horizonMonths * 365) / 12 / 7);
}

/**
 * D6 cancellation calibration (C3-I2, plan §7.7 step 5): pending-cancel intent
 * scales the drawn renewal rate by `1 − pendingShare × P(effective|requested)`
 * — expected non-renewal from intent is exactly `share × P`, never 1.0 (the
 * shared cancelHazard decision table, mirrored at the draw level). The factor
 * source is typed: `calibrated` draws P from a Jeffreys posterior over the
 * calibration sample (posterior uncertainty propagates); `declared_prior` is a
 * labeled constant. Absent evidence returns the identity — the draw stream is
 * untouched, so frozen bundles replay byte-identically.
 */
function pendingCancelRateFactor(
  pendingCancel: PendingCancelEvidence | undefined,
): (rng: Rng) => number {
  if (!pendingCancel) return () => 1;
  const { pendingIntentSubscriptions: pending, activeSubscriptions: active, calibration } = pendingCancel;
  const invalid = (detail: string): never => {
    throw new OfferContractError('pending_cancel_evidence_invalid', detail);
  };
  if (!Number.isInteger(pending) || pending < 0) invalid(`pendingIntentSubscriptions must be a non-negative integer (got ${pending})`);
  if (!Number.isInteger(active) || active <= 0) invalid(`activeSubscriptions must be a positive integer (got ${active})`);
  if (pending > active) invalid(`pendingIntentSubscriptions (${pending}) exceeds activeSubscriptions (${active})`);
  const p = calibration.pEffectiveGivenRequested;
  if (!Number.isFinite(p) || p < 0 || p > 1) invalid(`pEffectiveGivenRequested must be within [0, 1] (got ${p})`);
  if (calibration.source === 'calibrated') {
    if (!calibration.calibrationVersion || !calibration.calibrationVersion.trim()) {
      invalid('a calibrated source requires an immutable calibrationVersion');
    }
    if (!Number.isInteger(calibration.sampleSize) || calibration.sampleSize < 1) {
      invalid('a calibrated source requires a positive sampleSize');
    }
  } else if (!calibration.priorLabel || !calibration.priorLabel.trim()) {
    invalid('a declared prior requires a non-empty priorLabel — there is no silent constant');
  }
  const share = pending / active;
  // No pending intent ⇒ identity WITHOUT consuming the RNG stream — the
  // projection (and replay hash) is exactly the intent-free one.
  if (share === 0) return () => 1;
  if (calibration.source === 'declared_prior') {
    const factor = 1 - share * p;
    return () => factor;
  }
  const n = calibration.sampleSize;
  const k = p * n;
  return (rng) => 1 - share * sampleBeta(rng, k + 0.5, Math.max(n - k, 0) + 0.5);
}

function geometricCycles(renewalRate: number, horizonCycles: number): number {
  if (renewalRate <= 0) return 1;
  if (renewalRate >= 1) return horizonCycles;
  // 1 + r + r^2 + ... + r^(H-1): the first cycle is already paid.
  return (1 - Math.pow(renewalRate, horizonCycles)) / (1 - renewalRate);
}

function censusModel(evidence: RenewalCensusEvidence): OfferValueModel {
  const r1 = evidence.r1Denominator > 0 ? evidence.r1Numerator / evidence.r1Denominator : 0;
  const r2 = evidence.r2Denominator > 0 ? evidence.r2Numerator / evidence.r2Denominator : 0;
  // sBG needs r2 rising above r1 to identify heterogeneity; otherwise the
  // honest projection is a single geometric rate from the pooled census.
  const fallbackReason = r2 <= r1 ? ('r2_not_rising' as const) : undefined;
  const cancelFactor = pendingCancelRateFactor(evidence.pendingCancel);
  return {
    model: 'geometric',
    fallbackReason,
    draw(rng, horizonMonths) {
      // Jeffreys Beta(k + 0.5, n - k + 0.5) posterior over the pooled renewal rate.
      const pooledNum = evidence.r1Numerator + evidence.r2Numerator;
      const pooledDen = evidence.r1Denominator + evidence.r2Denominator;
      const rate = sampleBeta(rng, pooledNum + 0.5, Math.max(pooledDen - pooledNum, 0) + 0.5)
        * cancelFactor(rng);
      const cycles = geometricCycles(rate, horizonMonths);
      return evidence.firstPaymentArpu + evidence.renewalArpu * (cycles - 1);
    },
  };
}

/**
 * Weekly class (C2-I1): identical posterior mechanics to the monthly census
 * model — pooled Jeffreys beta over the renewal rate, `r2_not_rising` honesty
 * tag mirrored from the sbgModel guard — but the survival sum runs over
 * WEEKLY cycles derived from the horizon. Weekly evidence never mixes into a
 * monthly census (the caller supplies a weekly-only census by contract).
 */
function weeklyModel(evidence: WeeklyRenewalEvidence): OfferValueModel {
  const r1 = evidence.r1Denominator > 0 ? evidence.r1Numerator / evidence.r1Denominator : 0;
  const r2 = evidence.r2Denominator > 0 ? evidence.r2Numerator / evidence.r2Denominator : 0;
  const fallbackReason = r2 <= r1 ? ('r2_not_rising' as const) : undefined;
  const cancelFactor = pendingCancelRateFactor(evidence.pendingCancel);
  return {
    model: 'weekly_geometric',
    fallbackReason,
    draw(rng, horizonMonths) {
      const pooledNum = evidence.r1Numerator + evidence.r2Numerator;
      const pooledDen = evidence.r1Denominator + evidence.r2Denominator;
      const rate = sampleBeta(rng, pooledNum + 0.5, Math.max(pooledDen - pooledNum, 0) + 0.5)
        * cancelFactor(rng);
      const cycles = geometricCycles(rate, weeklyCyclesForHorizonMonths(horizonMonths));
      return evidence.firstPaymentArpu + evidence.renewalArpu * (cycles - 1);
    },
  };
}

function trialModel(evidence: TrialValueEvidence): OfferValueModel {
  // D3 decomposition (C0-AC-007): the paid-trial fee is a day-0 collection
  // from every trial starter, added ONCE outside the conversion share — never
  // folded into the underlying ARPU. Absent = free trial (fee 0, identical to
  // the pre-field behavior).
  const trialFeeValue = evidence.trialFeeValue ?? 0;
  if (
    evidence.trialFeeValue !== undefined
    && (!Number.isFinite(evidence.trialFeeValue) || evidence.trialFeeValue < 0)
  ) {
    throw new OfferContractError(
      'trial_fee_value_invalid',
      `trialFeeValue must be a finite value >= 0 (got ${evidence.trialFeeValue})`,
    );
  }
  return {
    model: 'trial_scaled',
    draw(rng, _horizonMonths, drawUnderlying) {
      if (!drawUnderlying) {
        throw new Error(
          `trial offer needs the underlying offer value (${evidence.underlyingOfferId}) at the same horizon`,
        );
      }
      const conversion = sampleBeta(
        rng,
        evidence.trialToPaidNumerator + 0.5,
        Math.max(evidence.trialToPaidDenominator - evidence.trialToPaidNumerator, 0) + 0.5,
      );
      return trialFeeValue + conversion * drawUnderlying(rng);
    },
  };
}

/**
 * One-time/lifetime class (D4, C3-I0): upfront semantics net of refunds —
 * a constant day-0 collection, no survival term, no renewal cycles. Mirrors
 * the shared model's D4 rule: zero is VALID (fully refunded buyers stay
 * entrants); a negative or non-finite value is a typed rejection.
 */
function oneTimeModel(evidence: OneTimeValueEvidence): OfferValueModel {
  if (!Number.isFinite(evidence.netValuePerBuyer) || evidence.netValuePerBuyer < 0) {
    throw new OfferContractError(
      'one_time_value_invalid',
      `one_time netValuePerBuyer must be a finite value >= 0 (got ${evidence.netValuePerBuyer}); refunds are clamped upstream, never signed here`,
    );
  }
  return {
    model: 'one_time',
    draw: () => evidence.netValuePerBuyer,
  };
}

export function buildOfferValueModel(evidence: OfferValueEvidence): OfferValueModel {
  switch (evidence.kind) {
    case 'renewal_census':
      return censusModel(evidence);
    case 'upfront':
      return {
        model: 'upfront',
        draw: () => evidence.upfrontValue,
      };
    case 'weekly_renewal':
      return weeklyModel(evidence);
    case 'trial':
      return trialModel(evidence);
    case 'one_time':
      return oneTimeModel(evidence);
    default: {
      const exhaustive: never = evidence;
      throw new Error(`Unknown value evidence kind: ${JSON.stringify(exhaustive)}`);
    }
  }
}
